import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/** The element a `#fragment` names on the page as rendered, if there is one. */
function hashTarget(hash: string): HTMLElement | null {
  return hash ? document.getElementById(hash.slice(1)) : null;
}

/**
 * A new page starts at the top. Router navigations keep the window's scroll
 * offset, so clicking a card halfway down the blog index opened the article
 * halfway down too, with the header and hero somewhere above the fold.
 *
 * Only for navigations that go somewhere new. Back and forward are left alone,
 * so the browser can put the reader back where they were. Jumps rather than
 * animates: the sheet sets `scroll-behavior: smooth`, which would otherwise run
 * the whole page the reader just left past them on the way up.
 *
 * A link to a section (the header's About, `/#about`) lands on that section
 * instead, because the router does not follow a fragment the way the browser
 * does. From another page it jumps, for the same reason as above; on the page
 * the section is already on, it glides there. Keyed on the location's `key`
 * as well, so following the same link twice still goes back to it.
 */
export function ScrollToTop() {
  const { pathname, hash, key } = useLocation();
  const navigationType = useNavigationType();
  const previousPathname = useRef(pathname);

  useEffect(() => {
    // The first render of a page load is a POP too, which is what leaves a
    // deep link or a refresh sitting where it was opened.
    if (navigationType === 'POP') return;
    // A fragment this page answers is the effect below's to scroll to.
    if (hashTarget(hash)) return;

    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname, hash, navigationType]);

  useEffect(() => {
    const samePage = previousPathname.current === pathname;
    previousPathname.current = pathname;
    if (navigationType === 'POP') return;

    hashTarget(hash)?.scrollIntoView({ behavior: samePage ? 'smooth' : 'instant', block: 'start' });
  }, [pathname, hash, key, navigationType]);

  return null;
}
